import { Modal, Button } from "react-bootstrap";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import {
  ATTENTION_REQUIRED_INTRO,
  ATTENTION_REQUIRED_TITLE,
  KPI_CONFLICT_LIST_MAX_HEIGHT,
  KPI_CONFLICT_MODAL_DESC_ID,
  KPI_CONFLICT_MODAL_TITLE_ID,
  LABEL_ACTIVITY_REMOVED,
  LABEL_AFFECTED_KPIS,
  LABEL_THESE_CALCULATED_KPIS_NEED_IT,
  REASON_ACTIVITY_REMOVED_FROM_TEMPLATE,
  VALIDATION_MODAL_DESC_ID,
  VALIDATION_MODAL_TITLE_ID,
} from "@features/admin/kpi/constants/kpiDependencyConflictConstants";
import { formatKpiConflictBulletLines } from "@features/admin/kpi/utils/kpiConflictFromError";
import {
  groupTemplateActivityRemovalBlocks,
  listNamedRemovedActivitiesForFallback,
} from "@features/admin/kpi/utils/groupTemplateActivityRemovalBlocks";
import { kpiDependentRowKey } from "@features/admin/kpi/utils/kpiDependentDisplay";
import {
  kpiConflictDismissLabel,
  kpiConflictParagraph,
  kpiConflictTitle,
} from "@features/admin/kpi/utils/kpiConflictModalMeta";

export default function ValidationErrorModal() {
  const { validationErrors, kpiConflict, dismiss } = useErrorModalStore();
  const show = validationErrors !== null || kpiConflict !== null;

  if (kpiConflict) {
    const title = kpiConflictTitle(kpiConflict.reason);
    const paragraph = kpiConflictParagraph(kpiConflict);
    const dismissLabel = kpiConflictDismissLabel(kpiConflict.reason);
    const isTemplateActivityRemoval =
      kpiConflict.reason === REASON_ACTIVITY_REMOVED_FROM_TEMPLATE;
    const blocks = isTemplateActivityRemoval
      ? groupTemplateActivityRemovalBlocks(kpiConflict.dependents)
      : [];
    const fallbackRemovedActivities =
      isTemplateActivityRemoval && blocks.length === 0
        ? listNamedRemovedActivitiesForFallback(kpiConflict.dependents, kpiConflict.subject)
        : [];

    return (
      <Modal
        show={show}
        onHide={dismiss}
        centered
        scrollable
        restoreFocus
        aria-labelledby={KPI_CONFLICT_MODAL_TITLE_ID}
        aria-describedby={KPI_CONFLICT_MODAL_DESC_ID}
      >
        <Modal.Header
          closeButton
          className="alert-warning border-warning mb-0"
        >
          <Modal.Title id={KPI_CONFLICT_MODAL_TITLE_ID} className="text-dark">
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div id={KPI_CONFLICT_MODAL_DESC_ID}>
            <p className="mb-3">{paragraph}</p>
            {isTemplateActivityRemoval ? (
              blocks.length > 0 ? (
                <div
                  style={{
                    maxHeight: KPI_CONFLICT_LIST_MAX_HEIGHT,
                    overflowY: "auto",
                    overflowWrap: "break-word",
                  }}
                >
                  {blocks.map((block) => (
                    <div key={`removed-${block.removedActivity.activity_id}`} className="mb-3">
                      {block.removedActivity.activity_name.trim() ? (
                        <p className="mb-2">
                          <span className="fw-semibold">{LABEL_ACTIVITY_REMOVED}</span>{" "}
                          {block.removedActivity.activity_name}
                        </p>
                      ) : null}
                      <p className="fw-semibold mb-2">{LABEL_THESE_CALCULATED_KPIS_NEED_IT}</p>
                      <ul className="mb-0">
                        {block.kpis.map((kpi) => (
                          <li key={`kpi-${block.removedActivity.activity_id}-${kpi.kpiId}`} className="mb-2">
                            <strong>{kpi.kpiName}</strong>
                            {kpi.usesLine ? (
                              <div className="text-muted small ms-0">{kpi.usesLine}</div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {fallbackRemovedActivities.map((ref) => (
                    <p key={`fallback-removed-${ref.activity_id}`} className="mb-2">
                      <span className="fw-semibold">{LABEL_ACTIVITY_REMOVED}</span> {ref.activity_name}
                    </p>
                  ))}
                </>
              )
            ) : kpiConflict.dependents.length > 0 ? (
              <>
                <p className="fw-semibold mb-2">{LABEL_AFFECTED_KPIS}</p>
                <ul
                  className="mb-0"
                  style={{
                    maxHeight: KPI_CONFLICT_LIST_MAX_HEIGHT,
                    overflowY: "auto",
                    overflowWrap: "break-word",
                  }}
                >
                  {kpiConflict.dependents.map((dep, index) => {
                    const sublines = formatKpiConflictBulletLines(dep, kpiConflict.reason);
                    return (
                      <li key={kpiDependentRowKey(dep, index)} className="mb-2">
                        <strong>{dep.kpi_name}</strong>
                        {sublines.map((line) => (
                          <div key={line} className="text-muted small ms-0">
                            {line}
                          </div>
                        ))}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex-column flex-sm-row gap-2">
          <Button
            variant="primary"
            className="w-100 w-sm-auto ms-sm-auto"
            onClick={dismiss}
          >
            {dismissLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={dismiss}
      centered
      scrollable
      restoreFocus
      aria-labelledby={VALIDATION_MODAL_TITLE_ID}
      aria-describedby={VALIDATION_MODAL_DESC_ID}
    >
      <Modal.Header closeButton className="alert-warning border-warning mb-0">
        <Modal.Title id={VALIDATION_MODAL_TITLE_ID} className="text-dark">
          {ATTENTION_REQUIRED_TITLE}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p id={VALIDATION_MODAL_DESC_ID}>{ATTENTION_REQUIRED_INTRO}</p>
        <ul className="mb-0">
          {validationErrors?.map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      </Modal.Body>
      <Modal.Footer className="flex-column flex-sm-row gap-2">
        <Button
          variant="primary"
          className="w-100 w-sm-auto ms-sm-auto"
          onClick={dismiss}
        >
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
